/**
 * M-A9 — Skill Import Wizard.
 *
 * Sprint 132 Stream D. 5-step modal that walks the user through importing
 * a third-party skill artefact (Anthropic SKILL.md, OpenAI Custom GPT JSON)
 * into a BYOS draft skill.
 *
 * Flow:
 *  1. Source       → pick format
 *  2. Upload       → drop a file (≤5 MB)
 *  3. Preview      → show ImportPreview, allow inline edits + show issues
 *  4. Confirm      → final summary + acknowledgement checkbox
 *  5. Result       → success link or error list
 *
 * Backend endpoint: POST /tenants/{tid}/skills/import (Sprint 132 Stream A).
 * The endpoint re-parses the uploaded file on the confirm call, so to make
 * inline edits effective we PATCH the freshly created skill afterwards
 * using `usePatchAdminSkill`.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Info,
  Sparkles,
  Upload,
} from 'lucide-react';
import {
  useImportTenantSkill,
  usePatchAdminSkill,
} from '@/features/skills';
import type {
  Skill,
  SkillCreate,
  SkillImportFormat,
  SkillImportIssue,
  SkillImportIssueLevel,
  SkillImportPreview,
} from '@/features/skills';
import { getErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const MAX_IMPORT_BYTES = 5 * 1024 * 1024; // 5 MB

type WizardStep = 'source' | 'upload' | 'preview' | 'confirm' | 'result';

const FORMATS: SkillImportFormat[] = [
  'anthropic',
  'openai_gpt',
  'm365',
  'openapi',
  'zip_native',
];

interface ComingSoonOption {
  value: string;
  label: string;
}

const COMING_SOON: ComingSoonOption[] = [];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function issueIcon(level: SkillImportIssueLevel) {
  switch (level) {
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
    case 'warning':
      return (
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      );
    default:
      return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
  }
}

function issueBadgeClass(level: SkillImportIssueLevel): string {
  switch (level) {
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'warning':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  }
}

interface EditableFields {
  display_name: string;
  description: string;
  vertical: string;
  function: string;
  category: string;
  tags: string; // comma-separated
}

function editableFromSkill(s: SkillCreate): EditableFields {
  return {
    display_name: s.display_name,
    description: s.description ?? '',
    vertical: s.vertical,
    function: s.function,
    category: s.category,
    tags: (s.tags ?? []).join(', '),
  };
}

function diffFields(
  base: SkillCreate,
  edits: EditableFields,
): Partial<SkillCreate> {
  const out: Partial<SkillCreate> = {};
  if (edits.display_name !== base.display_name) {
    out.display_name = edits.display_name;
  }
  const desc = edits.description.trim();
  if (desc !== (base.description ?? '')) {
    out.description = desc || null;
  }
  if (edits.vertical !== base.vertical) out.vertical = edits.vertical;
  if (edits.function !== base.function) out.function = edits.function;
  if (edits.category !== base.category) out.category = edits.category;
  const editTags = edits.tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const baseTags = base.tags ?? [];
  if (
    editTags.length !== baseTags.length ||
    editTags.some((t, i) => t !== baseTags[i])
  ) {
    out.tags = editTags;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImported?: (skill: Skill) => void;
}

export function SkillImportWizard({ open, onOpenChange, onImported }: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [step, setStep] = useState<WizardStep>('source');
  const [format, setFormat] = useState<SkillImportFormat>('anthropic');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SkillImportPreview | null>(null);
  const [edits, setEdits] = useState<EditableFields | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [importedSkill, setImportedSkill] = useState<Skill | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // ADR-114 enmienda 1: dirty = hay trabajo del usuario en curso (sin importar).
  const isDirty =
    open &&
    !importedSkill &&
    (!!file || !!preview || acknowledged || !!edits);

  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };

  const importMutation = useImportTenantSkill();
  const patchMutation = usePatchAdminSkill(importedSkill?.id ?? '');

  function reset() {
    setStep('source');
    setFormat('anthropic');
    setFile(null);
    setFileError(null);
    setPreview(null);
    setEdits(null);
    setAcknowledged(false);
    setImportedSkill(null);
    setResultError(null);
  }

  function closeNow() {
    onOpenChange(false);
    // Reset slightly later so animation completes.
    setTimeout(reset, 150);
  }

  function handleClose() {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    closeNow();
  }

  function handleFile(f: File | null) {
    setFile(f);
    if (!f) {
      setFileError(null);
      return;
    }
    if (f.size > MAX_IMPORT_BYTES) {
      setFileError(
        t('admin.skills.importWizard.fileTooLarge', {
          size: formatBytes(f.size),
        }),
      );
    } else if (f.size === 0) {
      setFileError(t('admin.skills.importWizard.fileEmpty'));
    } else {
      setFileError(null);
    }
  }

  async function handleDryRun() {
    if (!file || fileError) return;
    try {
      const resp = await importMutation.mutateAsync({
        file,
        format,
        dry_run: true,
      });
      setPreview(resp.preview);
      setEdits(editableFromSkill(resp.preview.proposed_skill));
      setStep('preview');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  const blockingIssues = useMemo(
    () => preview?.issues.filter((i) => i.level === 'error') ?? [],
    [preview],
  );

  async function handleConfirm() {
    if (!file || !preview || !edits || !acknowledged) return;
    if (blockingIssues.length > 0) return;
    try {
      const resp = await importMutation.mutateAsync({
        file,
        format,
        dry_run: false,
      });
      if (!resp.skill) {
        setResultError(t('admin.skills.importWizard.backendError'));
        setStep('result');
        return;
      }
      const skill = resp.skill;
      // Apply user edits (if any) via PATCH so inline overrides survive.
      const overrides = diffFields(preview.proposed_skill, edits);
      if (Object.keys(overrides).length > 0) {
        try {
          await patchMutation.mutateAsync(overrides);
        } catch (e) {
          // The skill exists; we just couldn't apply overrides.
          toast.warning(
            t('admin.skills.importWizard.editsNotSaved', {
              message: getErrorMessage(e),
            }),
          );
        }
      }
      setImportedSkill(skill);
      setResultError(null);
      setStep('result');
      onImported?.(skill);
    } catch (e) {
      setResultError(getErrorMessage(e));
      setStep('result');
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // Render helpers
  // ───────────────────────────────────────────────────────────────────────

  function renderSourceStep() {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          {FORMATS.map((value) => (
            <label
              key={value}
              className={cn(
                'flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors',
                format === value
                  ? 'border-primary bg-primary/5'
                  : 'border-input hover:bg-accent',
              )}
            >
              <input
                type="radio"
                name="skill-import-format"
                value={value}
                checked={format === value}
                onChange={() => setFormat(value)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {t(`admin.skills.importWizard.formats.${value}.label`)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t(`admin.skills.importWizard.formats.${value}.description`)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t(`admin.skills.importWizard.formats.${value}.hint`)}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="pt-2">
          <div className="text-xs text-muted-foreground mb-2">
            {t('admin.skills.importWizard.comingSoon')}
          </div>
          <div className="space-y-1">
            {COMING_SOON.map((c) => (
              <div
                key={c.value}
                className="flex items-center gap-2 p-2 border rounded-md opacity-50 text-sm"
              >
                <FileText className="h-4 w-4" />
                <span className="flex-1">{c.label}</span>
                <Badge variant="outline" className="text-xs">
                  {t('admin.skills.importWizard.sprint133')}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderUploadStep() {
    const formatLabels: Record<SkillImportFormat, string> = {
      anthropic: 'SKILL.md',
      openai_gpt: 'JSON',
      m365: 'manifest JSON',
      openapi: 'OpenAPI YAML / JSON',
      zip_native: 'Klviar Skill package',
    };
    const acceptByFormat: Record<SkillImportFormat, string> = {
      anthropic: '.md,.markdown,text/markdown',
      openai_gpt: '.json,application/json',
      m365: '.json,application/json',
      openapi: '.yaml,.yml,.json,application/json,application/x-yaml,text/yaml',
      zip_native: '.zip,application/zip',
    };
    return (
      <div className="space-y-3">
        <Label>
          {t('admin.skills.importWizard.uploadLabel', {
            fileType: formatLabels[format],
          })}
        </Label>
        <div
          className={cn(
            'border-2 border-dashed rounded-md p-6 text-center transition-colors',
            file
              ? 'border-primary bg-primary/5'
              : 'border-input hover:bg-accent',
          )}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0] ?? null;
            handleFile(f);
          }}
          data-testid="skill-import-dropzone"
        >
          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          {file ? (
            <div className="space-y-1">
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatBytes(file.size)}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {t('admin.skills.importWizard.dropzone')}
            </div>
          )}
          <input
            type="file"
            accept={acceptByFormat[format]}
            className="hidden"
            id="skill-import-file"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <label
            htmlFor="skill-import-file"
            className="inline-block mt-3 text-sm text-primary hover:underline cursor-pointer"
          >
            {file
              ? t('admin.skills.importWizard.replaceFile')
              : t('admin.skills.importWizard.chooseFile')}
          </label>
        </div>
        {fileError && (
          <div
            className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
            data-testid="skill-import-file-error"
          >
            <AlertCircle className="h-4 w-4" />
            {fileError}
          </div>
        )}
      </div>
    );
  }

  function renderPreviewStep() {
    if (!preview || !edits) return null;
    const proposed = preview.proposed_skill;
    const issues = preview.issues;
    return (
      <div className="space-y-4">
        <div className="rounded-md border p-3 bg-muted/40">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            {t('admin.skills.importWizard.proposedSkill')}
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {proposed.slug}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="imp-name">
              {t('admin.skills.importWizard.displayName')}
            </Label>
            <Input
              id="imp-name"
              value={edits.display_name}
              onChange={(e) =>
                setEdits({ ...edits, display_name: e.target.value })
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="imp-vertical">
              {t('admin.skills.importWizard.vertical')}
            </Label>
            <Input
              id="imp-vertical"
              value={edits.vertical}
              onChange={(e) =>
                setEdits({ ...edits, vertical: e.target.value })
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="imp-function">
              {t('admin.skills.importWizard.function')}
            </Label>
            <Input
              id="imp-function"
              value={edits.function}
              onChange={(e) =>
                setEdits({ ...edits, function: e.target.value })
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="imp-category">
              {t('admin.skills.importWizard.category')}
            </Label>
            <Input
              id="imp-category"
              value={edits.category}
              onChange={(e) =>
                setEdits({ ...edits, category: e.target.value })
              }
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="imp-tags">
              {t('admin.skills.importWizard.tags')}
            </Label>
            <Input
              id="imp-tags"
              value={edits.tags}
              onChange={(e) => setEdits({ ...edits, tags: e.target.value })}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="imp-desc">
              {t('admin.skills.importWizard.description')}
            </Label>
            <Textarea
              id="imp-desc"
              rows={3}
              value={edits.description}
              onChange={(e) =>
                setEdits({ ...edits, description: e.target.value })
              }
            />
          </div>
        </div>

        {issues.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-2">
              {t('admin.skills.importWizard.issues', {
                count: issues.length,
              })}
            </div>
            <div className="space-y-2" data-testid="skill-import-issues">
              {issues.map((iss: SkillImportIssue, idx: number) => (
                <div
                  key={`${iss.code}-${idx}`}
                  className="flex items-start gap-2 p-2 border rounded-md text-sm"
                >
                  {issueIcon(iss.level)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={cn(
                          'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                          issueBadgeClass(iss.level),
                        )}
                      >
                        {iss.level}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {iss.code}
                      </span>
                      {iss.field && (
                        <span className="font-mono text-xs text-muted-foreground">
                          @{iss.field}
                        </span>
                      )}
                    </div>
                    <div className="mt-1">{iss.message}</div>
                    {iss.suggestion && (
                      <div className="text-xs text-muted-foreground mt-1">
                        → {iss.suggestion}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {preview.proposed_assets.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-2">
              {t('admin.skills.importWizard.assets', {
                count: preview.proposed_assets.length,
              })}
            </div>
            <div className="space-y-1">
              {preview.proposed_assets.map((a) => (
                <div
                  key={a.asset_id}
                  className="flex items-center gap-2 p-2 border rounded-md text-sm"
                >
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-xs flex-1 truncate">
                    {a.asset_id}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {a.type}
                    {typeof a.size_bytes === 'number'
                      ? ` · ${formatBytes(a.size_bytes)}`
                      : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {blockingIssues.length > 0 && (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
            {t('admin.skills.importWizard.blocking', {
              count: blockingIssues.length,
              s: blockingIssues.length === 1 ? '' : 's',
            })}
          </div>
        )}
      </div>
    );
  }

  function renderConfirmStep() {
    if (!preview || !edits) return null;
    return (
      <div className="space-y-3">
        <div className="rounded-md border p-3 space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">
              {t('admin.skills.importWizard.slug')}
            </span>{' '}
            <span className="font-mono">{preview.proposed_skill.slug}</span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('admin.skills.importWizard.tier')}
            </span>{' '}
            byos_skill
            <span className="text-muted-foreground">
              {' '}
              · {t('admin.skills.importWizard.lifecycle')}
            </span>{' '}
            draft
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('admin.skills.importWizard.name')}
            </span>{' '}
            {edits.display_name}
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('admin.skills.importWizard.verticalFunction')}
            </span>{' '}
            {edits.vertical} · {edits.function}
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('admin.skills.importWizard.tagsConfirm')}
            </span>{' '}
            {edits.tags || (
              <span className="italic">
                {t('admin.skills.importWizard.none')}
              </span>
            )}
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1"
            data-testid="skill-import-ack"
          />
          <span>
            {t('admin.skills.importWizard.ack1')}
            <strong>draft</strong>
            {t('admin.skills.importWizard.ack2')}
          </span>
        </label>
      </div>
    );
  }

  function renderResultStep() {
    if (resultError) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">
              {t('admin.skills.importWizard.importFailed')}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">{resultError}</div>
        </div>
      );
    }
    if (!importedSkill) return null;
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">
            {t('admin.skills.importWizard.importedAsDraft')}
          </span>
        </div>
        <div className="rounded-md border p-3 text-sm">
          <div className="font-mono text-xs text-muted-foreground">
            {importedSkill.slug}
          </div>
          <div className="font-medium mt-1">{importedSkill.display_name}</div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────
  // Footer / step machinery
  // ───────────────────────────────────────────────────────────────────────

  const stepIndex: Record<WizardStep, number> = {
    source: 1,
    upload: 2,
    preview: 3,
    confirm: 4,
    result: 5,
  };

  function renderFooter() {
    switch (step) {
      case 'source':
        return (
          <>
            <Button variant="outline" onClick={handleClose}>
              {t('admin.skills.importWizard.cancel')}
            </Button>
            <Button onClick={() => setStep('upload')}>
              {t('admin.skills.importWizard.next')}
            </Button>
          </>
        );
      case 'upload':
        return (
          <>
            <Button variant="outline" onClick={() => setStep('source')}>
              {t('admin.skills.importWizard.back')}
            </Button>
            <Button
              onClick={handleDryRun}
              disabled={!file || !!fileError || importMutation.isPending}
              data-testid="skill-import-preview-btn"
            >
              {importMutation.isPending
                ? t('admin.skills.importWizard.parsing')
                : t('admin.skills.importWizard.preview')}
            </Button>
          </>
        );
      case 'preview':
        return (
          <>
            <Button variant="outline" onClick={() => setStep('upload')}>
              {t('admin.skills.importWizard.back')}
            </Button>
            <Button
              onClick={() => setStep('confirm')}
              disabled={blockingIssues.length > 0}
              data-testid="skill-import-confirm-step-btn"
            >
              {t('admin.skills.importWizard.continue')}
            </Button>
          </>
        );
      case 'confirm':
        return (
          <>
            <Button variant="outline" onClick={() => setStep('preview')}>
              {t('admin.skills.importWizard.back')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={
                !acknowledged ||
                blockingIssues.length > 0 ||
                importMutation.isPending
              }
              data-testid="skill-import-final-btn"
            >
              {importMutation.isPending
                ? t('admin.skills.importWizard.importing')
                : t('admin.skills.importWizard.confirmImport')}
            </Button>
          </>
        );
      case 'result':
        return (
          <>
            {importedSkill ? (
              <>
                <Button variant="outline" onClick={handleClose}>
                  {t('admin.skills.importWizard.close')}
                </Button>
                <Button
                  onClick={() => {
                    handleClose();
                    navigate(
                      `/skills/${encodeURIComponent(importedSkill.slug)}`,
                    );
                  }}
                >
                  {t('admin.skills.importWizard.openSkill')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep('upload')}>
                  {t('admin.skills.importWizard.tryAgain')}
                </Button>
                <Button onClick={handleClose}>
                  {t('admin.skills.importWizard.close')}
                </Button>
              </>
            )}
          </>
        );
    }
  }

  function stepTitle(): string {
    switch (step) {
      case 'source':
        return t('admin.skills.importWizard.stepSource');
      case 'upload':
        return t('admin.skills.importWizard.stepUpload');
      case 'preview':
        return t('admin.skills.importWizard.stepPreview');
      case 'confirm':
        return t('admin.skills.importWizard.stepConfirm');
      case 'result':
        return importedSkill
          ? t('admin.skills.importWizard.stepResultOk')
          : t('admin.skills.importWizard.stepResultFailed');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent
        className="sm:max-w-[640px]"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
        data-testid="skill-import-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('admin.skills.importWizard.title')}
          </DialogTitle>
          <DialogDescription>
            {t('admin.skills.importWizard.stepOf', {
              step: stepIndex[step],
              title: stepTitle(),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {step === 'source' && renderSourceStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'confirm' && renderConfirmStep()}
          {step === 'result' && renderResultStep()}
        </div>

        <DialogFooter className="gap-2">{renderFooter()}</DialogFooter>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          closeNow();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}

export default SkillImportWizard;
